"use client";
import React, {useEffect, useRef, useState} from "react";
import {SDialogFC, SDialogProps} from "@/Master/SDialogFC/SDialogFC";
import {FontIcon, Input, Popover, Toast} from "@/app/_components";
import {checkPhoneNumber, CountryCodeEnum} from "@/app/utils/validateForm";
import {InputRef} from "@/app/_components/Input/input";
import $http from "@/app/_api";
import {SDialogManagerFC} from "@/Master/SDialogFC/SDialogManagerFC";
import {ForcedLoginDialogFC} from "@/app/(pay)/UI/ForcedLoginDialog/ForcedLoginDialog";
import {useDialogMethods} from "@/Master/SDialogFC/useDialogMethods";
import Util from "@/Master/Util";
import ShopMgr from "@/app/(shop)/ShopMgr";
import AuthTrack, {CheckVisitorPhoneResultScene, LoginScene, PhoneInputScene} from "@/Core/AuthTrack";
import styles from "@/app/(auth)/page.module.css";

const arrowIconStyle = {
    fontSize: ".35rem",
    transform: "rotate(90deg)",
    marginBottom: "-0.055rem",
    marginLeft: ".15rem",
    display: "inline-block",
};

interface VisitorRechargeDialogProps extends SDialogProps {
    callMobileOK: () => void;
}

export const VisitorRechargeDialogFC: React.FC<VisitorRechargeDialogProps> = (props) => {
    const { fcKey, callMobileOK,onClose } = props;

    const {closeDialog} = useDialogMethods(fcKey);

    const [areaNo, setAreaNo] = useState<string>(CountryCodeEnum.US_CA);
    const [mobileError, setMobileError] = useState<boolean>(false);
    const [mobileFormat, setMobileFormat] = useState<boolean>(false);

    const inputRef = useRef<InputRef>(null);
    const areaNoRef = useRef<HTMLSpanElement>(null);

    // 初始化时，从本地存储中获取游客手机号设置到输入框中
    if (Util.isLog){
        console.log(ShopMgr.getVisitorMobile);
    }

    useEffect(() => {
        if (areaNoRef.current && ShopMgr.getVisitorAreaNo) {
            areaNoRef.current.innerText = '+' + ShopMgr.getVisitorAreaNo;
        } else {
            areaNoRef.current?.innerText && (areaNoRef.current.innerText = '+' + CountryCodeEnum.US_CA);
        }

        if (ShopMgr.getVisitorMobile) {
            inputRef.current?.setValue(ShopMgr.getVisitorMobile)
            if (checkPhoneNumber(ShopMgr.getVisitorMobile, ShopMgr.getVisitorAreaNo)) {
                setMobileFormat(true);
                setMobileError(false);
            } else {
                setMobileFormat(false);
            }
        }

        // console.log("VisitorRechargeDialog initialized");

        return () => {
            // console.log("VisitorRechargeDialog disposed");
        };
    }, []);

    const onCloseCall = () => {
        onClose?.();
        closeDialog();
    }

    const handleChangeAreaNo = (newAreaNo: string) => {
        setAreaNo(newAreaNo);
        if (areaNoRef.current) {
            areaNoRef.current.innerText = '+' + newAreaNo;
        }
        const mobile = inputRef.current?.getValue() || '';
        if (checkPhoneNumber(mobile, newAreaNo)) {
            setMobileFormat(true);
            setMobileError(false);
        } else {
            setMobileFormat(false);
        }
    };

    const handleSetMobile = (mobile: string) => {
        inputRef.current?.setValue(mobile);
        if (checkPhoneNumber(mobile, areaNo)) {
            setMobileFormat(true);
        } else {
            setMobileFormat(false);
        }
    };

    const onMobileBar = (mobile: string) => {
        if (checkPhoneNumber(mobile, areaNo)) {
            AuthTrack.inputPhone(PhoneInputScene.PhoneVerify);
        }
    };

    const onGotoLogin = () => {
        onCloseCall();
        SDialogManagerFC.openDialog(ForcedLoginDialogFC,{
            fcKey:SDialogManagerFC.getFCKey(ForcedLoginDialogFC),
            onClose:()=>{
                AuthTrack.showLogin(LoginScene.visitorRecharge);
            }
        });
    }

    const handleSubmit = async () => {
        AuthTrack.clickSubmit();
        // 正则校验手机号
        const mobile = inputRef.current?.getValue();
        if (!mobile || !checkPhoneNumber(mobile, areaNo) ) {
            setMobileError(true);
            return;
        };
        setMobileError(false);

        // 接口校验手机号是否已注册
        AuthTrack.sendPhoneVerify();
        const result = await $http.Auth.checkPhoneRegister({areaCode: areaNo, mobile});
        if (!result) return;

        ShopMgr.setVisitorAreaNo(areaNo);
        ShopMgr.setVisitorMobile(mobile);

        // 根据接口返回手机号是否注册字段执行不同逻辑
        const {isRegistered} = result;
        if (isRegistered) {
            AuthTrack.sendPhoneVerifyResult(CheckVisitorPhoneResultScene.Registered)
            onGotoLogin();
        } else{
            AuthTrack.sendPhoneVerifyResult(CheckVisitorPhoneResultScene.Unregistered)
            Toast.show({
                content: "This phone number is not registered",
                duration: 2000,
            });
            onCloseCall();
            callMobileOK();
        }
    };

    return (
        <SDialogFC
            fcKey={fcKey}
            mode="Center"
            buttons={[
                {
                    label: "Submit",
                    onClick: handleSubmit,
                    style: mobileFormat ? "blueBtn" : "grayBtn",
                },
            ]}
            isClickMaskClose={false}
            // onClose={props.onClose}
        >
            <section>
                <section>
                    Currently in visitor mode, to ensure you have never registered an account, you will need to verify
                    that the mobile phone number is in use.
                </section>
                <section className={styles.visitor_recharge_input}>
                    <div className={styles.prefix_wrapper}>
                        <div className={styles.icon_wrapper}>
                            <FontIcon icon="s-phone" style={{color: "#9E94A5", verticalAlign: "middle"}}/>
                        </div>
                        <div style={{color: "#9E94A5"}}>
                            <Popover
                                items={Object.values(CountryCodeEnum)}
                                onChange={handleChangeAreaNo}
                                style={{left: '-0.2rem', marginTop: '-0.05rem'}}
                            >
                                <span className={styles.area_code_box} style={{marginLeft: ".2rem"}}>
                                    <span ref={areaNoRef} className={styles.te}>
                                        +{areaNo}
                                    </span>
                                    <FontIcon icon="s-right" style={arrowIconStyle}/>
                                </span>
                            </Popover>
                        </div>
                        <div style={{flex: 1, marginLeft: ".3rem", color: "#333"}}>
                            <Input
                                ref={inputRef}
                                type="tel"
                                placeholder="Enter your phone number"
                                onChange={(e) => handleSetMobile(e.target.value)}
                                onBlur={(e) => onMobileBar(e.target.value)}
                            />
                        </div>
                    </div>
                </section>
                {mobileError && <p className={styles.error_phone}>Incorrect phone number</p>}
            </section>
        </SDialogFC>
    );
};
